import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  APPLICATION_STATUSES,
  APPLICATION_STATUS_MAP,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  SearchBox,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { api, qs } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';
import { useDebounced, useListState } from '../../lib/hooks';

interface ApplicationCard {
  id: string;
  stage: string;
  stageLabel: string;
  stageKind: string;
  stageAction: string | null;
  status: string;
  statusLabel: string;
  statusKind: string;
  appliedAt: string;
  stageChangedAt: string;
  candidateId: string;
  candidateCode: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  headline: string | null;
  source: string;
  sourceLabel: string;
  cvAssetId: string | null;
  job: { id: string; title: string; code: string | null };
  campaign: { id: string; name: string } | null;
  answered: number;
}

interface CampaignLite {
  id: string;
  name: string;
}

interface JobLite {
  id: string;
  title: string;
}

/**
 * طلبات التقديم.
 *
 * ## ولماذا بطاقاتٌ لا صفوفٌ في جدول، وهي نفسُها التي في المسار
 *
 * المسارُ يجيب «أين وقف كلُّ طلب» — أعمدةٌ تُقرأ عرضًا ويُرى فيها التكدّس.
 * وهذه الشاشةُ تجيب سؤالًا آخر: «من قدّم؟» — قائمةٌ تُقرأ طولًا، تُرشَّح
 * بالوظيفة والحملة والمصدر، ويُقرأ في كلّ بطاقةٍ من هو وعلى ماذا قدّم
 * وفي أيّ حملة.
 *
 * والبطاقةُ لا تعيد ما في الملفّ: تقول ما يُقرَّر به «أفتحه أم لا» — اسمٌ
 * وصفةٌ ووظيفةٌ وحملةٌ ومصدرٌ وكم سؤالًا أجاب. والباقي خلف نقرة.
 */
export function Applications() {
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);

  const query = {
    jobPostId: state.jobPostId || undefined,
    campaignId: state.campaignId || undefined,
    stage: state.stage || undefined,
    status: state.status || undefined,
    q: debounced || undefined,
  };

  const list = useQuery({
    queryKey: ['recruit', 'applications', query],
    queryFn: () => api<ApplicationCard[]>(`/hr/recruit/applications${qs(query)}`),
  });

  const campaigns = useQuery({
    queryKey: ['recruit', 'campaigns'],
    queryFn: () => api<CampaignLite[]>('/hr/recruit/campaigns'),
  });

  const jobs = useQuery({
    queryKey: ['recruit', 'jobs', {}],
    queryFn: () => api<JobLite[]>('/hr/recruit/jobs'),
  });

  const stages = useQuery({
    queryKey: ['recruit', 'stages'],
    queryFn: () =>
      api<{ key: string; label: string; isActive: boolean }[]>(
        '/hr/recruit/stages',
      ),
  });

  const rows = list.data ?? [];
  const active = rows.filter((a) => a.status === 'ACTIVE');
  const hired = rows.filter((a) => a.status === 'HIRED');
  const withCv = rows.filter((a) => a.cvAssetId !== null);

  return (
    <div className="page">
      <PageHead
        title="طلبات التقديم"
        subtitle="من قدّم، وعلى أيّ وظيفة، وفي أيّ حملة"
        actions={
          <Link className="btn" to="/hr/recruit/pipeline">
            <Icon name="funnel" size={15} />
            المسار
          </Link>
        }
      />

      <div className="toolbar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الهاتف أو البريد أو كود المرشّح"
        />

        <select
          className="select"
          value={state.jobPostId ?? ''}
          onChange={(e) => patch({ jobPostId: e.target.value || undefined })}
        >
          <option value="">كل الوظائف</option>
          {(jobs.data ?? []).map((j) => (
            <option key={j.id} value={j.id}>
              {j.title}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={state.campaignId ?? ''}
          onChange={(e) => patch({ campaignId: e.target.value || undefined })}
        >
          <option value="">كل الحملات</option>
          {(campaigns.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={state.stage ?? ''}
          onChange={(e) => patch({ stage: e.target.value || undefined })}
        >
          <option value="">كل المراحل</option>
          {/* الأعمدةُ من الخادم: مسارُ كلّ شركةٍ لها. */}
          {(stages.data ?? [])
            .filter((st) => st.isActive)
            .map((st) => (
              <option key={st.key} value={st.key}>
                {st.label}
              </option>
            ))}
        </select>

        <select
          className="select"
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
        >
          <option value="">كل الحالات</option>
          {APPLICATION_STATUSES.map((st) => (
            <option key={st} value={st}>
              {APPLICATION_STATUS_MAP[st].label}
            </option>
          ))}
        </select>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {rows.length > 0 && (
        <div className="stats">
          <Stat label="الطلبات المعروضة" value={num(rows.length)} />
          <Stat
            label="ما زالت تتحرّك"
            value={num(active.length)}
            note="لم تُستبعد ولم تُعيَّن بعد"
          />
          <Stat label="عُيّن" value={num(hired.length)} />
          <Stat
            label="أرفق سيرةً ذاتية"
            value={num(withCv.length)}
            note={
              rows.length > 0
                ? `${num(Math.round((withCv.length / rows.length) * 100))}٪ ممّن قدّم`
                : undefined
            }
          />
        </div>
      )}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا طلبات"
          text="لم يصل طلبٌ يطابق هذه المرشّحات. وطلباتُ الرابط العلنيّ تصل هنا في لحظتها بلا إدخالٍ من أحد."
        />
      )}

      {/*
        السقفُ يُقال ولا يُخفى: الخادمُ يردّ خمسمئةً، ومن يظنّ أنّه يرى
        الكلَّ وهو يرى نصفَه يقرأ رقمًا خاطئًا في الأعلى.
      */}
      {rows.length >= 500 && (
        <p className="tiny mute">
          هذه أحدثُ خمسمئة طلبٍ — ضيّق بالوظيفة أو الحملة لترى ما وراءها.
        </p>
      )}

      {rows.length > 0 && (
        <div className="rc-cards">
          {rows.map((a) => (
            <ApplicationTile key={a.id} card={a} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * بطاقةُ طلب.
 *
 * الصفةُ تحت الاسم لا الوظيفةُ: «مصمّم بخبرة ستّ سنوات» هي ما كتبه هو عن
 * نفسه، والوظيفةُ هي ما قدّم عليه — والخلطُ بينهما يجعل عشرين بطاقةً تقول
 * الشيءَ نفسَه.
 */
function ApplicationTile({ card: a }: { card: ApplicationCard }) {

  return (
    <Link className="rc-card" to={`/hr/recruit/applications/${a.id}`}>
      <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <strong>{a.name}</strong>
        <Badge kind={a.stageKind}>{a.stageLabel}</Badge>
        {a.status !== 'ACTIVE' && (
          <Badge kind={a.statusKind}>{a.statusLabel}</Badge>
        )}
      </div>

      {a.headline && <span className="tiny mute">{a.headline}</span>}

      <div className="rc-form-peek">
        <span className="rc-chip">{a.job.title}</span>
        {a.campaign && <span className="rc-chip">{a.campaign.name}</span>}
        <span className="rc-chip">{a.sourceLabel}</span>
      </div>

      <dl className="detail-list">
        <dt>قدّم في</dt>
        <dd className="num">{date(a.appliedAt)}</dd>

        <dt>آخر حركة</dt>
        <dd className="num">{date(a.stageChangedAt)}</dd>

        <dt>كود المرشّح</dt>
        <dd className="num">{a.candidateCode ?? '—'}</dd>

        <dt>للتواصل</dt>
        <dd className="num" dir="ltr">
          {a.phone ?? a.email ?? '—'}
        </dd>

        <dt>أجاب عن</dt>
        <dd>
          {a.answered > 0 ? (
            plural(a.answered, WORDS.question)
          ) : (
            <span className="mute">لا أسئلة</span>
          )}
        </dd>

        <dt>السيرة الذاتية</dt>
        <dd>
          {a.cvAssetId ? (
            <span>مرفقة</span>
          ) : (
            <span className="mute">لم تُرفق</span>
          )}
        </dd>
      </dl>

      {/* الخطوةُ التالية تُقرأ على البطاقة: القائمةُ تصير قائمةَ عملٍ لا سجلًّا. */}
      {a.status === 'ACTIVE' && a.stageAction && (
        <span className="tiny" style={{ color: 'var(--brand-700)' }}>
          <Icon name="arrowLeft" size={12} /> {a.stageAction}
        </span>
      )}
    </Link>
  );
}
